import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormHelperText,
  InputAdornment,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Autocomplete,
  Paper,
  alpha,
  Stack,
  Chip,
  Divider,
  ListSubheader,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  LinearProgress,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { facilitiesService } from "../../../services/facilities.service";
import { kitchenService } from "../../../services/kitchen.service";
import { posSellableItemsService } from "../../../services/pos-sellable-items.service";
import { inventoryService } from "../../../services/inventory.service";
import { convertUnits } from "../../../utils/units";
import { POSSellableItem } from "../../../services/pos-sellable-items.service";
import { IESDoc } from "../../../../../app.interface";
import { bomService } from "../../../services/bom.service";
import { BillOfMaterial } from "../BOM/types";
import { POSSellableItemSummary } from "./components/POSSellableItemSummary";
import { ExtendedBillOfMaterial } from "./components/POSSellableItemSummary";
import {
  IngredientDialog,
  IngredientOption,
  IngredientEntry,
} from "./components/IngredientDialog";
import {
  ModifiedIngredient,
  ModifiedProcessedItem,
  ProcessedItemEntry,
  ProcessedItemDialogEntry,
} from "./types";
import {
  processedItemsService,
  ConsumeProcessedItemsRequest,
} from "../../../services/processed-items.service";
import {
  Batch,
  ListBatchesParams,
  UseIngredientsRequest,
} from "../../../services/inventory.service";
import { ProcessedItemDialog, ProcessedItemOption } from "../BOM/components/ProcessedItemDialog";
import { Stepper as CustomStepper } from "../../../../../components/Stepper/Stepper";
import { THEME_COLORS } from "../BOM/BOMForm";

// Extend the inventory service interface
declare module "../../../services/inventory.service" {
  interface InventoryService {
    listProcessedItems(params: {
      pageIndex: number;
      pageSize: number;
      sortField: string;
      sortOrder: string;
    }): Promise<{
      data: ProcessedItemOption[];
      total: number;
    }>;
    getProcessedItem(id: string): Promise<{
      data: {
        processed_item_id: string;
        name: string;
        quantity: number;
        unit: string;
        total_cost: number;
        cost_per_unit: number;
      };
    }>;
  }
}

// Extend the kitchen service interface
declare module "../../../services/kitchen.service" {
  interface KitchenService {
    listProcessedItems(params: {
      pageIndex: number;
      pageSize: number;
      sortField: string;
      sortOrder: string;
    }): Promise<{
      data: ProcessedItemOption[];
      total: number;
    }>;
  }
}

interface SellableItemFormData {
  name: string;
  api_batch_id: string;
  production_date: number;
  expiry_date: number;
  storage_location: string;
  storage_notes: string;
  category: string;
  recipe: {
    name: string;
    id: string;
    base_recipe_cost: number;
    recipe_multiplier: number;
  };
  ingredients: any[];
  total_api_cost: number;
  api_cost_distribution: number;
  total_cost: number;
}

interface POSSellableItemFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  initialData: null;
  mode: "add";
}

interface IngredientBatchResult {
  ingredient_id: string;
  batch: {
    _id: string;
    batch_id: string;
    quantity: number;
    unit: string;
    cost_per_unit: number;
  };
}

const generateBatchNumber = () => {
  const timestamp = new Date().getTime().toString(36);
  const random = Math.random().toString(36).substring(2, 5);
  return `PROC-${timestamp}-${random}`.toUpperCase();
};

export const POSSellableItemFormDialog: FC<POSSellableItemFormDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [useAutoBatchNumber, setUseAutoBatchNumber] = useState(true);
  const [facilities, setFacilities] = useState<{
    branches: any[];
    warehouses: any[];
  }>({
    branches: [],
    warehouses: [],
  });
  const [boms, setBoms] = useState<BillOfMaterial[]>([]);
  const [selectedBom, setSelectedBom] = useState<ExtendedBillOfMaterial | null>(null);
  const [modifiedIngredients, setModifiedIngredients] = useState<ModifiedIngredient[]>([]);
  const [multiplier, setMultiplier] = useState(1);
  const [bomLoading, setBomLoading] = useState(false);
  const [costCalculating, setCostCalculating] = useState(false);
  const [processedItems, setProcessedItems] = useState<ProcessedItemOption[]>([]);
  const [selectedProcessedItems, setSelectedProcessedItems] = useState<ProcessedItemEntry[]>([]);
  const [loadingProcessedItems, setLoadingProcessedItems] = useState(false);
  const [modifiedProcessedItems, setModifiedProcessedItems] = useState<ModifiedProcessedItem[]>([]);

  const [formData, setFormData] = useState<SellableItemFormData>({
    name: "",
    api_batch_id: generateBatchNumber(),
    production_date: dayjs().valueOf(),
    expiry_date: dayjs().add(7, "day").valueOf(),
    storage_location: "",
    storage_notes: "",
    category: "",
    recipe: {
      name: "",
      id: "",
      base_recipe_cost: 0,
      recipe_multiplier: 1,
    },
    ingredients: [],
    total_api_cost: 0,
    api_cost_distribution: 0,
    total_cost: 0,
  });

  // Add new state for dialogs and sections
  const [processedItemDialogOpen, setProcessedItemDialogOpen] = useState(false);
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [processedItemsSectionExpanded, setProcessedItemsSectionExpanded] = useState(false);
  const [rawMaterialsSectionExpanded, setRawMaterialsSectionExpanded] = useState(false);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<number | null>(null);
  const [editingIngredient, setEditingIngredient] = useState<ModifiedIngredient | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [facilitiesResponse, bomsResponse] = await Promise.all([
        Promise.all([
          facilitiesService.listBranches({}),
          facilitiesService.listWarehouses({}),
        ]),
        bomService.listBOMs({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc",
        }),
      ]);

      setFacilities({
        branches: facilitiesResponse[0].data,
        warehouses: facilitiesResponse[1].data,
      });
      setBoms(bomsResponse.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load required data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      api_batch_id: generateBatchNumber(),
    }));
    setUseAutoBatchNumber(true);
    setSelectedBom(null);
  }, [open]);

  useEffect(() => {
    const loadProcessedItems = async () => {
      try {
        setLoadingProcessedItems(true);
        const response = await kitchenService.listProcessedItems({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc",
        });
        console.log("Loaded processed items:", response.data);
        setProcessedItems(response.data);
      } catch (error) {
        console.error("Error loading processed items:", error);
        toast.error("Failed to load processed items");
      } finally {
        setLoadingProcessedItems(false);
      }
    };

    if (open) {
      loadProcessedItems();
    }
  }, [open]);

  const handleBomChange = async (bom: BillOfMaterial | null) => {
    console.log("handleBomChange called with BOM:", bom);
    setBomLoading(true);

    try {
      // Convert BOM to ExtendedBillOfMaterial
      const extendedBom = bom
        ? ({
            ...bom,
            yield_quantity: bom.ingredients[0]?.quantity || 1,
            yield_unit: bom.ingredients[0]?.unit || "units",
            base_cost: bom.price || 0,
          } as ExtendedBillOfMaterial)
        : null;

      setSelectedBom(extendedBom);
      if (extendedBom) {
        // Set the name from BOM only if the user hasn't customized it
        setFormData((prev) => ({
          ...prev,
          name: prev.name || extendedBom.name || "",
          recipe: {
            name: extendedBom.name || "",
            id: extendedBom._id || "",
            base_recipe_cost: extendedBom.base_cost,
            recipe_multiplier: 1,
          },
        }));

        // Get all ingredients' batches first
        const ingredientBatchesPromises = extendedBom.ingredients.map(async (ing) => {
          try {
            const response = await inventoryService.listBatches({
              ingredient_id: ing.item_id,
              pageIndex: 0,
              pageSize: 100,
              sortField: "expiry_date",
              sortOrder: "asc",
              include_wasted: false,
            });

            // Get the first available batch that has enough quantity
            const availableBatch = response.data.batches.find((batch: Batch) => 
              batch.remaining_quantity >= (ing.quantity || 0)
            );

            if (availableBatch) {
              return {
                ingredient_id: ing.ingredient_id,
                batch: {
                  _id: availableBatch._id,
                  batch_id: availableBatch.batch_id,
                  quantity: ing.quantity || 0,
                  unit: ing.unit,
                  cost_per_unit: availableBatch.cost_per_unit || 0
                }
              } as IngredientBatchResult;
            }
            return null;
          } catch (error) {
            console.error(`Error fetching batches for ingredient ${ing.ingredient_id}:`, error);
            return null;
          }
        });

        const ingredientBatches = await Promise.all(ingredientBatchesPromises);

        // Set ingredients with their batches
        setModifiedIngredients(
          extendedBom.ingredients.map(
            (ing: {
              item_id: string;
              ingredient_id: string;
              brand_name: string;
              quantity: number;
              unit: string;
              notes?: string;
            }) => {
              const matchingBatch = ingredientBatches.find(
                (batch: IngredientBatchResult | null) => batch?.ingredient_id === ing.ingredient_id
              );

              const quantity = ing.quantity || 0;
              return {
                item_id: ing.item_id,
                item_type: "ingredient",
                ingredient_id: ing.ingredient_id,
                brand_name: ing.brand_name,
                modified_quantity: quantity,
                original_quantity: quantity,
                unit: ing.unit,
                notes: ing.notes || "",
                selected_batches: matchingBatch ? [matchingBatch.batch] : [],
                is_modified: false,
                required_quantity: quantity * multiplier,
                remaining_quantity: matchingBatch ? 0 : (quantity * multiplier), // 0 if batch is allocated
                base_unit: ing.unit
              };
            }
          )
        );

        // First, get all processed items
        const response = await kitchenService.listProcessedItems({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc",
        });

        console.log("All processed items from kitchen service:", response.data);

        // Map processed items from BOM with their details
        const processedItemsWithCost = await Promise.all(
          (extendedBom.processed_items || []).map(
            async (item: {
              processed_item_id: string;
              name: string;
              consumbtion_quantity: number;
              consumbtion_unit: string;
            }) => {
              // Find the processed item details
              const processedItem = response.data.find(
                (pi) => pi._id === item.processed_item_id
              );
              console.log(
                "Found processed item for",
                item.name,
                ":",
                processedItem
              );

              if (!processedItem) {
                console.warn(
                  `Could not find processed item with ID ${item.processed_item_id}`
                );
                return {
                  processed_item_id: item.processed_item_id,
                  name: item.name,
                  modified_quantity: item.consumbtion_quantity || 0,
                  original_quantity: item.consumbtion_quantity || 0,
                  unit: item.consumbtion_unit,
                  notes: "",
                  selected_batches: [],
                  is_modified: false,
                  required_quantity:
                    (item.consumbtion_quantity || 0) * multiplier,
                  remaining_quantity:
                    (item.consumbtion_quantity || 0) * multiplier,
                  total_cost: 0,
                } as ModifiedProcessedItem;
              }

              const quantity = item.consumbtion_quantity || 0;
              const cost_per_unit = processedItem.cost_per_unit || 0;
              const total_cost = cost_per_unit * quantity;

              // Create a batch entry for the processed item
              const batch = {
                _id: item.processed_item_id,
                batch_id: item.processed_item_id,
                quantity: quantity,
                unit: item.consumbtion_unit,
                cost_per_unit: cost_per_unit
              };

              console.log("Cost calculation for", item.name, ":", {
                quantity,
                cost_per_unit,
                total_cost,
                multiplier,
                processedItemDetails: {
                  cost_per_unit: processedItem.cost_per_unit,
                  total_cost: processedItem.total_cost,
                },
              });

              return {
                processed_item_id: item.processed_item_id,
                name: item.name,
                modified_quantity: quantity,
                original_quantity: quantity,
                unit: item.consumbtion_unit,
                notes: "",
                selected_batches: [batch], // Initialize with the batch
                is_modified: false,
                required_quantity: quantity * multiplier,
                remaining_quantity: 0, // Set to 0 since we're allocating all at once
                total_cost: total_cost * multiplier,
              } as ModifiedProcessedItem;
            }
          )
        );

        console.log("Final processed items with cost:", processedItemsWithCost);
        setModifiedProcessedItems(processedItemsWithCost);
      } else {
        setModifiedIngredients([]);
        setModifiedProcessedItems([]);
        setMultiplier(1);
        setFormData((prev) => ({
          ...prev,
          name: "",
          recipe: {
            name: "",
            id: "",
            base_recipe_cost: 0,
            recipe_multiplier: 1,
          },
        }));
      }
    } catch (error) {
      console.error("Error in handleBomChange:", error);
      toast.error("Failed to load recipe details");
    } finally {
      setBomLoading(false);
    }
  };

  const handleBatchNumberChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      batch_id: value,
    }));
    if (useAutoBatchNumber) setUseAutoBatchNumber(false);
  };

  const handleDateChange = (field: string, value: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetBatchNumber = () => {
    setFormData((prev) => ({
      ...prev,
      batch_id: generateBatchNumber(),
    }));
    setUseAutoBatchNumber(true);
  };

  const handleIngredientQuantityChange = (index: number, value: number) => {
    setModifiedIngredients((prev) =>
      prev.map((ingredient, i) =>
        i === index
          ? {
              ...ingredient,
              modified_quantity: value,
              required_quantity: value,
              remaining_quantity:
                value -
                ingredient.selected_batches.reduce((total, batch) => {
                  const convertedQuantity =
                    batch.unit !== ingredient.unit
                      ? convertUnits(
                          batch.quantity,
                          batch.unit,
                          ingredient.unit
                        ) || batch.quantity
                      : batch.quantity;
                  return total + convertedQuantity;
                }, 0),
              is_modified: value !== ingredient.original_quantity * multiplier,
            }
          : ingredient
      )
    );
  };

  const handleMultiplierChange = (value: number) => {
    setMultiplier(value);
    setModifiedIngredients((prev) =>
      prev.map((ingredient) => ({
        ...ingredient,
        modified_quantity: ingredient.original_quantity * value,
        required_quantity: ingredient.original_quantity * value,
        remaining_quantity:
          ingredient.original_quantity * value -
          ingredient.selected_batches.reduce((total, batch) => {
            const convertedQuantity =
              batch.unit !== ingredient.unit
                ? convertUnits(batch.quantity, batch.unit, ingredient.unit) ||
                  batch.quantity
                : batch.quantity;
            return total + convertedQuantity;
          }, 0),
      }))
    );
    // Update processed items with multiplier
    setModifiedProcessedItems((prev) =>
      prev.map((item) => ({
        ...item,
        modified_quantity: item.original_quantity * value,
        required_quantity: item.original_quantity * value,
        remaining_quantity:
          item.original_quantity * value -
          item.selected_batches.reduce((total, batch) => {
            const convertedQuantity =
              batch.unit !== item.unit
                ? convertUnits(batch.quantity, batch.unit, item.unit) ||
                  batch.quantity
                : batch.quantity;
            return total + convertedQuantity;
          }, 0),
        total_cost:
          (item.total_cost / item.modified_quantity) *
          (item.original_quantity * value),
      }))
    );
  };

  const handleStorageChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      storage_location: value,
    }));
  };

  const handleNotesChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      storage_notes: value,
    }));
  };

  const handleUnitChange = (index: number, unit: string) => {
    setModifiedIngredients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], unit };
      return updated;
    });
  };

  const handleBatchSelect = async (
    ingredientIndex: number,
    batchId: string,
    quantity: number,
    unit: string
  ) => {
    try {
      // Fetch the batch data to get the cost per unit
      const response = await inventoryService.listBatches({
        ingredient_id: batchId,
        pageIndex: 0,
        pageSize: 100,
        sortField: "expiry_date",
        sortOrder: "asc",
        include_wasted: false,
      });

      const batch = response.data.batches.find(
        (b: any) => b.batch_id === batchId
      );
      const cost_per_unit = batch?.cost_per_unit || 0;

      setModifiedIngredients((prev) => {
        const ingredient = prev[ingredientIndex];
        const existingBatchIndex = ingredient.selected_batches.findIndex(
          (b) => b.batch_id === batchId
        );
        let newBatches;

        if (existingBatchIndex >= 0) {
          // Update existing batch
          if (quantity <= 0) {
            // Remove batch if quantity is 0 or negative
            newBatches = ingredient.selected_batches.filter(
              (b) => b.batch_id !== batchId
            );
          } else {
            newBatches = ingredient.selected_batches.map((b, i) =>
              i === existingBatchIndex ? { ...b, quantity, unit } : b
            );
          }
        } else {
          // Add new batch
          newBatches = [
            ...ingredient.selected_batches,
            {
              _id: batch._id,
              batch_id: batch.batch_id,
              quantity,
              unit,
              cost_per_unit,
            },
          ];
        }

        // Calculate remaining quantity
        const totalSelectedQuantity = newBatches.reduce((total, batch) => {
          // Convert batch quantity to ingredient's base unit if different
          const convertedQuantity =
            batch.unit !== ingredient.unit
              ? convertUnits(batch.quantity, batch.unit, ingredient.unit) ||
                batch.quantity
              : batch.quantity;
          return total + convertedQuantity;
        }, 0);

        return prev.map((ing, i) =>
          i === ingredientIndex
            ? {
                ...ing,
                selected_batches: newBatches,
                remaining_quantity:
                  ing.required_quantity - totalSelectedQuantity,
                is_modified: totalSelectedQuantity !== ing.required_quantity,
              }
            : ing
        );
      });
    } catch (error) {
      console.error("Error fetching batch data:", error);
    }
  };

  const handleProcessedItemQuantityChange = (index: number, value: number) => {
    setModifiedProcessedItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const newQuantity = value;
        const costPerUnit = item.total_cost / item.modified_quantity;
        const newTotalCost = costPerUnit * newQuantity;

        // Create a single batch entry for the processed item
        const newBatch = {
          _id: item.processed_item_id,
          batch_id: item.processed_item_id,
          quantity: newQuantity,
          unit: item.unit,
          cost_per_unit: costPerUnit
        };

        return {
          ...item,
          modified_quantity: newQuantity,
          required_quantity: newQuantity,
          remaining_quantity: 0, // Since we're allocating all at once
          total_cost: newTotalCost,
          is_modified: newQuantity !== item.original_quantity * multiplier,
          selected_batches: [newBatch]
        };
      })
    );
  };

  const handleProcessedItemsSave = async (items: ProcessedItemDialogEntry[]) => {
    console.log(
      "Saving processed items - full items array:",
      JSON.stringify(items, null, 2)
    );

    const processedItemsWithCost = items.map((item) => {
      if (!item.item) return null;

      console.log("Processing item:", {
        name: item.item.name,
        _id: item.item._id,
        cost_per_unit: item.item.cost_per_unit,
        quantity: item.quantity,
        full_item: JSON.stringify(item.item, null, 2),
      });

      const itemTotalCost = (item.item.cost_per_unit || 0) * item.quantity;

      // Create a batch entry for the new processed item
      const batch = {
        _id: item.item._id,
        batch_id: item.item._id,
        quantity: item.quantity,
        unit: item.unit,
        cost_per_unit: item.item.cost_per_unit || 0
      };

      const modifiedItem: ModifiedProcessedItem = {
        processed_item_id: item.item._id,
        name: item.item.name,
        modified_quantity: item.quantity,
        original_quantity: item.quantity,
        unit: item.unit,
        notes: item.notes,
        selected_batches: [batch],
        is_modified: false,
        required_quantity: item.quantity,
        remaining_quantity: 0, // Since we're allocating all at once
        total_cost: itemTotalCost,
      };

      console.log(
        "Created modified item:",
        JSON.stringify(modifiedItem, null, 2)
      );
      return modifiedItem;
    });

    const filteredItems = processedItemsWithCost.filter(
      (item): item is ModifiedProcessedItem => item !== null
    );
    console.log("New processed items:", JSON.stringify(filteredItems, null, 2));

    // Merge new items with existing ones, avoiding duplicates
    const existingItems = [...modifiedProcessedItems];
    filteredItems.forEach((newItem) => {
      const existingIndex = existingItems.findIndex(
        (existing) => existing.processed_item_id === newItem.processed_item_id
      );

      if (existingIndex >= 0) {
        // Update existing item
        existingItems[existingIndex] = {
          ...existingItems[existingIndex],
          modified_quantity:
            existingItems[existingIndex].modified_quantity +
            newItem.modified_quantity,
          required_quantity:
            existingItems[existingIndex].required_quantity +
            newItem.required_quantity,
          remaining_quantity:
            existingItems[existingIndex].remaining_quantity +
            newItem.remaining_quantity,
          total_cost:
            existingItems[existingIndex].total_cost + newItem.total_cost,
          is_modified: true,
        };
      } else {
        // Add new item
        existingItems.push(newItem);
      }
    });

    console.log(
      "Final merged processed items:",
      JSON.stringify(existingItems, null, 2)
    );
    setModifiedProcessedItems(existingItems);
    setProcessedItemDialogOpen(false);
  };

  const handleProcessedItemsCancel = () => {
    setProcessedItemDialogOpen(false);
  };

  const handleAddProcessedItem = () => {
    // Reset any previously selected items in the dialog
    setSelectedProcessedItems([]);
    setProcessedItemDialogOpen(true);
  };

  const handleRemoveProcessedItem = (index: number) => {
    setModifiedProcessedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddIngredient = () => {
    setEditingIngredientIndex(null);
    setEditingIngredient(null);
    setIngredientDialogOpen(true);
  };

  const handleEditIngredient = (index: number) => {
    const ingredient = modifiedIngredients[index];
    setEditingIngredientIndex(index);
    setEditingIngredient(ingredient);
    setIngredientDialogOpen(true);
  };

  const handleIngredientsCancel = () => {
    setIngredientDialogOpen(false);
    setEditingIngredientIndex(null);
    setEditingIngredient(null);
  };

  const handleIngredientsSave = (items: IngredientEntry[]) => {
    console.log(
      "Saving ingredients - full items array:",
      JSON.stringify(items, null, 2)
    );

    // Map the ingredients to the format expected by the UI
    const newIngredients: ModifiedIngredient[] = items.map((item) => {
      if (!item.item) {
        console.warn("Item or item.item is undefined:", item);
        return null;
      }

      const mappedIngredient: ModifiedIngredient = {
        item_id: item.item.item_id || "",
        item_type: "raw_material",
        ingredient_id: item.item._id || "",
        brand_name: item.item.brand_name || "",
        modified_quantity: item.quantity,
        original_quantity: item.quantity,
        unit: item.unit,
        notes: item.notes || "",
        selected_batches: item.selected_batches?.map((batch) => ({
          _id: batch.batch_id || "",
          batch_id: batch.batch_id || "",
          quantity: batch.quantity || 0,
          unit: batch.unit || "",
          cost_per_unit: batch.cost_per_unit || 0,
        })) || [],
        is_modified: false,
        base_unit: item.item.base_unit,
        required_quantity: item.required_quantity || item.quantity,
        remaining_quantity: item.remaining_quantity || item.quantity,
      };
      return mappedIngredient;
    }).filter((ingredient): ingredient is ModifiedIngredient => ingredient !== null);

    if (editingIngredientIndex !== null) {
      // Update existing ingredient
      setModifiedIngredients((prev) => {
        const updated = [...prev];
        const newIngredient = newIngredients[0]; // We expect only one ingredient when editing
        if (newIngredient) {
          updated[editingIngredientIndex] = {
            ...updated[editingIngredientIndex],
            ...newIngredient,
            is_modified: true,
          };
        }
        return updated;
      });
    } else {
      // Add new ingredients
      setModifiedIngredients((prev) => {
        const existingIngredients = [...prev];
        newIngredients.forEach((newIngredient) => {
          const existingIndex = existingIngredients.findIndex(
            (existing) => existing.ingredient_id === newIngredient.ingredient_id
          );

          if (existingIndex >= 0) {
            // Update existing ingredient
            existingIngredients[existingIndex] = {
              ...existingIngredients[existingIndex],
              modified_quantity:
                existingIngredients[existingIndex].modified_quantity +
                newIngredient.modified_quantity,
              required_quantity:
                existingIngredients[existingIndex].required_quantity +
                newIngredient.required_quantity,
              remaining_quantity:
                existingIngredients[existingIndex].remaining_quantity +
                newIngredient.remaining_quantity,
              is_modified: true,
              selected_batches: [
                ...existingIngredients[existingIndex].selected_batches,
                ...newIngredient.selected_batches,
              ],
            };
          } else {
            // Add new ingredient
            existingIngredients.push(newIngredient);
          }
        });
        return existingIngredients;
      });
    }

    setIngredientDialogOpen(false);
    setEditingIngredientIndex(null);
    setEditingIngredient(null);
  };

  const handleRemoveIngredient = (index: number) => {
    setModifiedIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateAllocationProgress = (item: ModifiedIngredient | ModifiedProcessedItem) => {
    if (!item.required_quantity || item.required_quantity === 0) return 0;
    
    const totalAllocated = item.selected_batches.reduce((total, batch) => {
      const convertedQuantity = batch.unit !== item.unit
        ? convertUnits(batch.quantity, batch.unit, item.unit) || batch.quantity
        : batch.quantity;
      return total + convertedQuantity;
    }, 0);

    return Math.min(Math.round((totalAllocated / item.required_quantity) * 100), 100);
  };

  const steps = [
    {
      id: "01",
      label: "Basic Details",
      description: "Choose a Bill of Materials and set basic details",
      icon: "restaurant_menu",
      component: (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  label="Name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: 'text.secondary' }}>edit</Icon>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Enter sellable item name"
                  helperText={selectedBom ? `Default: ${selectedBom.name}` : ""}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  label="Category"
                >
                  <MenuItem value="main_course">Main Course</MenuItem>
                  <MenuItem value="appetizer">Appetizer</MenuItem>
                  <MenuItem value="dessert">Dessert</MenuItem>
                  <MenuItem value="beverage">Beverage</MenuItem>
                  <MenuItem value="side_dish">Side Dish</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Select Bill of Materials
              </Typography>
              <Autocomplete
                options={boms}
                loading={bomLoading}
                value={selectedBom}
                onChange={(_, bom) => handleBomChange(bom)}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BOM"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {bomLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      ),
      optional: false,
      validation: () => !!selectedBom && !!formData.category,
    },
    {
      id: "02",
      label: "Components",
      description: "Configure processed items and raw materials",
      icon: "category",
      component: (
        <Box>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              cursor: "pointer",
              '&:hover': { bgcolor: alpha(THEME_COLORS.primary, 0.04) },
            }}
            onClick={() => setProcessedItemsSectionExpanded(!processedItemsSectionExpanded)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon sx={{ color: THEME_COLORS.primary }}>
                {processedItemsSectionExpanded ? "expand_less" : "expand_more"}
              </Icon>
              <Typography variant="subtitle1" sx={{ color: THEME_COLORS.primary, fontWeight: 700 }}>
                Processed Items & Raw Materials ({modifiedProcessedItems.length + modifiedIngredients.length})
              </Typography>
            </Stack>
          </Paper>

          {processedItemsSectionExpanded && (
            <Box sx={{ pl: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recipe Multiplier
                </Typography>
                <TextField
                  fullWidth
                  label="Multiplier"
                  type="number"
                  value={multiplier}
                  onChange={(e) =>
                    handleMultiplierChange(parseFloat(e.target.value) || 1)
                  }
                  InputProps={{
                    inputProps: { min: 0.1, step: 0.1 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: 'text.secondary' }}>
                          straighten
                        </Icon>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon sx={{ color: THEME_COLORS.primary }}>category</Icon>
                    <Typography variant="subtitle1" color={THEME_COLORS.primary} fontWeight="600">
                      Processed Items
                    </Typography>
                    <Chip 
                      label={modifiedProcessedItems.length}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(THEME_COLORS.primary, 0.1),
                        color: THEME_COLORS.primary,
                        fontWeight: 500,
                      }}
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => {
                      setSelectedProcessedItems([]);
                      setProcessedItemDialogOpen(true);
                    }}
                    sx={{
                      borderColor: THEME_COLORS.primary,
                      color: THEME_COLORS.primary,
                      '&:hover': {
                        borderColor: THEME_COLORS.primary,
                        bgcolor: alpha(THEME_COLORS.primary, 0.04),
                      },
                    }}
                  >
                    Add Item
                  </Button>
                </Box>

                {modifiedProcessedItems.map((item, index) => (
                  <Paper
                    key={item.processed_item_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 1,
                      borderColor: (theme) =>
                        item.is_modified
                          ? THEME_COLORS.secondary
                          : THEME_COLORS.border.main,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        item.is_modified
                          ? alpha(THEME_COLORS.secondary, 0.04)
                          : 'background.paper',
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="600">
                            {item.name}
                            {item.is_modified && (
                              <Chip
                                label="Modified"
                                size="small"
                                color="warning"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Required: {item.required_quantity} {item.unit}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProcessedItem(index)}
                          sx={{ color: THEME_COLORS.danger, bgcolor: alpha(THEME_COLORS.danger, 0.04) }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantity"
                        value={item.modified_quantity}
                        onChange={(e) =>
                          handleProcessedItemQuantityChange(
                            index,
                            parseFloat(e.target.value) || 0
                          )
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {item.unit}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ bgcolor: 'background.paper' }}
                      />

                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={calculateAllocationProgress(item)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(THEME_COLORS.primary, 0.08),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: THEME_COLORS.primary
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Allocation Progress
                          </Typography>
                          <Typography variant="caption" color={THEME_COLORS.primary} fontWeight="500">
                            {calculateAllocationProgress(item)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Box>

              {processedItemDialogOpen && (
                <ProcessedItemDialog
                  open={processedItemDialogOpen}
                  onClose={handleProcessedItemsCancel}
                  onSave={handleProcessedItemsSave}
                  processedItems={processedItems}
                  loadingItems={loadingProcessedItems}
                  mode="add"
                  currentItems={modifiedProcessedItems.map((item) => ({
                    processed_item_id: item.processed_item_id,
                    quantity: item.modified_quantity,
                    unit: item.unit,
                  }))}
                />
              )}

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
                    <Typography variant="subtitle1" color={THEME_COLORS.primary} fontWeight="600">
                      Raw Materials
                    </Typography>
                    <Chip 
                      label={modifiedIngredients.length}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(THEME_COLORS.primary, 0.1),
                        color: THEME_COLORS.primary,
                        fontWeight: 500,
                      }}
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon>add</Icon>}
                    onClick={handleAddIngredient}
                    sx={{
                      borderColor: THEME_COLORS.primary,
                      color: THEME_COLORS.primary,
                      '&:hover': {
                        borderColor: THEME_COLORS.primary,
                        bgcolor: alpha(THEME_COLORS.primary, 0.04),
                      },
                    }}
                  >
                    Add Material
                  </Button>
                </Box>

                {modifiedIngredients.map((ingredient, index) => (
                  <Paper
                    key={ingredient.ingredient_id}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 1,
                      borderColor: (theme) =>
                        ingredient.is_modified
                          ? THEME_COLORS.secondary
                          : THEME_COLORS.border.main,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        ingredient.is_modified
                          ? alpha(THEME_COLORS.secondary, 0.04)
                          : 'background.paper',
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="600">
                            {ingredient.brand_name}
                            {ingredient.is_modified && (
                              <Chip
                                label="Modified"
                                size="small"
                                color="warning"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Required: {ingredient.required_quantity} {ingredient.unit}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleEditIngredient(index)}
                            sx={{ 
                              mr: 1,
                              color: THEME_COLORS.primary,
                              bgcolor: alpha(THEME_COLORS.primary, 0.04),
                            }}
                          >
                            <Icon>edit</Icon>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveIngredient(index)}
                            sx={{ 
                              color: THEME_COLORS.danger,
                              bgcolor: alpha(THEME_COLORS.danger, 0.04),
                            }}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </Box>
                      </Box>

                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={calculateAllocationProgress(ingredient)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(THEME_COLORS.primary, 0.08),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: THEME_COLORS.primary
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Allocation Progress
                          </Typography>
                          <Typography variant="caption" color={THEME_COLORS.primary} fontWeight="500">
                            {calculateAllocationProgress(ingredient)}%
                          </Typography>
                        </Box>
                      </Box>

                      {ingredient.selected_batches.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            Selected Batches
                          </Typography>
                          <Stack spacing={1}>
                            {ingredient.selected_batches.map((batch) => (
                              <Box
                                key={batch._id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                                  border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight="500">
                                      #{batch._id.slice(-6)} • {batch.quantity} {batch.unit}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {batch.cost_per_unit?.toFixed(3)} BHD/{batch.unit}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color={THEME_COLORS.primary} fontWeight="500">
                                    {(batch.quantity * batch.cost_per_unit)?.toFixed(3)} BHD
                                  </Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {Boolean(ingredientDialogOpen) && (
            <IngredientDialog
              open={ingredientDialogOpen}
              onClose={handleIngredientsCancel}
              onSave={handleIngredientsSave}
              mode={editingIngredientIndex !== null ? "edit" : "add"}
              currentItems={editingIngredientIndex !== null 
                ? [{
                    ingredient_id: modifiedIngredients[editingIngredientIndex].ingredient_id,
                    quantity: modifiedIngredients[editingIngredientIndex].modified_quantity,
                    unit: modifiedIngredients[editingIngredientIndex].unit
                  }]
                : modifiedIngredients.map(item => ({
                    ingredient_id: item.ingredient_id,
                    quantity: item.modified_quantity,
                    unit: item.unit
                  }))
              }
            />
          )}
        </Box>
      ),
      optional: false,
      validation: () =>
        modifiedIngredients.length > 0 || modifiedProcessedItems.length > 0,
    },
  ];

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < activeStep) return "complete";
    if (stepIndex === activeStep) return "current";
    return "upcoming";
  };

  const stepperSteps = steps.map((step, index) => ({
    id: step.id,
    name: step.label,
    description: step.description,
    status: getStepStatus(index) as "complete" | "current" | "upcoming",
    icon: step.icon,
  }));

  const handleStepClick = (stepId: string) => {
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
    }
  };

  const handleNext = () => {
    if (steps[activeStep].validation()) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      toast.error("Please complete all required fields before proceeding");
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      if (!selectedBom) {
        toast.error("Please select a Bill of Materials");
        return;
      }

      // First, validate all ingredients have sufficient quantities
      for (const ingredient of modifiedIngredients) {
        console.log("Validating ingredient:", {
          brand_name: ingredient.brand_name,
          ingredient_id: ingredient.ingredient_id,
          item_id: ingredient.item_id,
          selected_batches: ingredient.selected_batches,
        });

        // Get current batch quantities
        const batchRequestParams = {
          ingredient_id: ingredient.item_id,
          pageIndex: 0,
          pageSize: 100,
          sortField: "expiry_date",
          sortOrder: "asc" as "asc",
          include_wasted: false,
        };

        console.log(
          "Requesting batches with params:",
          JSON.stringify(batchRequestParams, null, 2)
        );

        try {
          const batchResponse = await inventoryService.listBatches(
            batchRequestParams
          );

          console.log(
            "Raw API response:",
            JSON.stringify(batchResponse, null, 2)
          );

          // Check if we have a valid response structure
          const batches = batchResponse?.data?.batches || [];

          if (!Array.isArray(batches)) {
            console.error("Invalid batch response structure:", batchResponse);
            toast.error(
              `Failed to validate batches for ${ingredient.brand_name}`
            );
            setSaving(false);
            return;
          }

          if (!batches.length) {
            console.warn("No batches found for ingredient:", {
              ingredient_id: ingredient.ingredient_id,
              brand_name: ingredient.brand_name,
              response: JSON.stringify(batchResponse, null, 2),
            });
            toast.error(
              `No available batches found for ${ingredient.brand_name}. Please ensure there are active batches for this ingredient.`
            );
            setSaving(false);
            return;
          }

          // Create a map of batch quantities using batch_id
          const batchQuantities = new Map();
          console.log("Available batches:", JSON.stringify(batches, null, 2));

          batches.forEach(
            (batch: {
              _id: string;
              batch_id: string;
              remaining_quantity: number;
              unit: string;
              expiry_date: string;
              is_wasted: boolean;
            }) => {
              if (!batch.batch_id) {
                console.warn("Batch missing batch_id:", batch);
                return;
              }

              // Skip wasted batches
              if (batch.is_wasted) {
                console.warn("Skipping wasted batch:", batch.batch_id);
                return;
              }

              batchQuantities.set(batch.batch_id, {
                _id: batch._id,
                remaining_quantity: batch.remaining_quantity,
                unit: batch.unit,
                expiry_date: batch.expiry_date,
                is_expired: new Date(batch.expiry_date) < new Date(),
              });
              console.log(`Added batch ${batch.batch_id} to map:`, {
                remaining_quantity: batch.remaining_quantity,
                unit: batch.unit,
                expiry_date: batch.expiry_date,
                is_expired: new Date(batch.expiry_date) < new Date(),
              });
            }
          );

          // Check each selected batch using batch_id
          for (const selectedBatch of ingredient.selected_batches) {
            console.log("Validating selected batch:", {
              batch_id: selectedBatch.batch_id,
              quantity: selectedBatch.quantity,
              available_batches: Array.from(batchQuantities.keys()),
            });

            const batchInfo = batchQuantities.get(selectedBatch.batch_id);

            if (!batchInfo) {
              console.warn(
                `No batch info found for batch ID: ${selectedBatch.batch_id}`
              );
              console.warn(
                "Available batch IDs:",
                Array.from(batchQuantities.keys())
              );
              toast.error(
                `Batch #${selectedBatch.batch_id.slice(
                  -6
                )} not found or is wasted for ${ingredient.brand_name}`
              );
              setSaving(false);
              return;
            }

            // Update the _id in the selected batch to match the current batch info
            selectedBatch._id = batchInfo._id;

            const availableQuantity = batchInfo.remaining_quantity;

            console.log("Checking batch quantities:", {
              batch_id: selectedBatch.batch_id,
              requested_quantity: selectedBatch.quantity,
              requested_unit: selectedBatch.unit,
              available_quantity: availableQuantity,
              available_unit: batchInfo.unit,
              is_expired: batchInfo.is_expired,
            });

            // Convert quantities to the same unit if they are different
            let requestedQuantityInBatchUnit = selectedBatch.quantity;
            if (selectedBatch.unit !== batchInfo.unit) {
              const converted = convertUnits(
                selectedBatch.quantity,
                selectedBatch.unit,
                batchInfo.unit
              );
              if (converted === null) {
                toast.error(
                  `Cannot convert between units: ${selectedBatch.unit} to ${batchInfo.unit} for ${ingredient.brand_name}`
                );
                setSaving(false);
                return;
              }
              requestedQuantityInBatchUnit = converted;
              console.log("Converted quantities:", {
                original: selectedBatch.quantity,
                originalUnit: selectedBatch.unit,
                converted: requestedQuantityInBatchUnit,
                convertedUnit: batchInfo.unit,
              });
            }

            if (requestedQuantityInBatchUnit > availableQuantity) {
              toast.error(
                `Insufficient quantity for ${
                  ingredient.brand_name
                }. Batch #${selectedBatch.batch_id.slice(
                  -6
                )} has only ${availableQuantity} ${batchInfo.unit} available.`
              );
              setSaving(false);
              return;
            }

            // Show warning for expired batches
            if (batchInfo.is_expired) {
              toast.warning(
                `Warning: Batch #${selectedBatch.batch_id.slice(-6)} for ${
                  ingredient.brand_name
                } has expired on ${new Date(
                  batchInfo.expiry_date
                ).toLocaleDateString()}. Proceed with caution.`
              );
            }
          }
        } catch (error) {
          console.error("Error validating batch quantities:", error);
          toast.error(
            `Failed to validate batch quantities for ${ingredient.brand_name}`
          );
          setSaving(false);
          return;
        }
      }

      // Calculate total costs
      const processedItemsTotalCost = modifiedProcessedItems.reduce(
        (total: number, item: ModifiedProcessedItem) =>
          total + (item.total_cost || 0),
        0
      );

      const rawMaterialsTotalCost = modifiedIngredients.reduce(
        (total: number, ingredient: ModifiedIngredient) =>
          total +
          ingredient.selected_batches.reduce(
            (
              batchTotal: number,
              batch: { quantity: number; cost_per_unit: number }
            ) =>
              batchTotal + (batch.quantity || 0) * (batch.cost_per_unit || 0),
            0
          ),
        0
      );

      const totalCost =
        Number((processedItemsTotalCost + rawMaterialsTotalCost).toFixed(3)) ||
        0;

      console.log("Cost calculation:", {
        processedItemsTotalCost,
        rawMaterialsTotalCost,
        totalCost,
      });

      // Rearrange the order - create sellable item first, then use ingredients
      // Create the sellable item
      const response = await posSellableItemsService.createPOSSellableItem({
        name: selectedBom.name,
        description: selectedBom.description || "No description available",
        category: formData.category,
        total_cost: totalCost,
        price: selectedBom.price || 0,
        ingredients: modifiedIngredients.map((ingredient) => ({
          item_id: ingredient.item_id,
          ingredient_id: ingredient.ingredient_id,
          quantity: ingredient.modified_quantity,
          unit: ingredient.unit,
          item_type: ingredient.item_type,
        })),
        processed_items: modifiedProcessedItems.map((item) => ({
          processed_item_id: item.processed_item_id,
          name: item.name,
          quantity: item.modified_quantity,
          unit: item.unit,
        })),
      });

      console.log("Sellable item created successfully:", response.data);

      // After successful creation, use the ingredients only if there are any batches to use
      const ingredientsWithBatches = modifiedIngredients.flatMap((ingredient) =>
        ingredient.selected_batches.map((batch) => ({
          _id: batch._id,
          batch_id: batch.batch_id,
          quantity: batch.quantity,
          unit: batch.unit,
          notes: `Used in sellable item: ${selectedBom.name}`,
        }))
      );

      // Only call the API if there are ingredients with batches
      if (ingredientsWithBatches.length > 0) {
        const useIngredientsRequest: UseIngredientsRequest = {
          ingredients: ingredientsWithBatches,
        };

        console.log("Sending useIngredients request:", useIngredientsRequest);

        // Call the API to use ingredients
        const useIngredientsResponse = await inventoryService.useIngredients(
          useIngredientsRequest
        );
        console.log("useIngredients response:", useIngredientsResponse);
      } else {
        console.log(
          "No ingredients with batches to use, skipping useIngredients API call"
        );
      }

      // Handle processed items consumption
      const processedItemsToConsume = modifiedProcessedItems
        .filter((item) => item.modified_quantity > 0)
        .map((item) => ({
          _id: item.processed_item_id,
          quantity: item.modified_quantity,
          notes: `Used in sellable item: ${selectedBom.name}`,
        }));

      // Only call the API if there are processed items to consume
      if (processedItemsToConsume.length > 0) {
        const consumeProcessedItemsRequest: ConsumeProcessedItemsRequest = {
          items: processedItemsToConsume,
        };

        console.log(
          "Sending consumeProcessedItems request:",
          consumeProcessedItemsRequest
        );

        // Call the API to consume processed items
        const consumeProcessedItemsResponse =
          await processedItemsService.consumeProcessedItems(
            consumeProcessedItemsRequest
          );
        console.log(
          "consumeProcessedItems response:",
          consumeProcessedItemsResponse
        );
      } else {
        console.log(
          "No processed items to consume, skipping consumeProcessedItems API call"
        );
      }

      toast.success("Sellable item created successfully");
      onClose();
      const updatedData = {
        ...response.data,
        total_cost: totalCost,
      };
      onSave(updatedData);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: "90vh",
          overflow: "hidden",
          bgcolor: "#F8FAFC",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.1)",
            }}
          >
            <Icon>restaurant</Icon>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Create Sellable Item
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Create a new sellable menu item
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Box sx={{ bgcolor: "white", borderBottom: 1, borderColor: "divider" }}>
        <CustomStepper steps={stepperSteps} onStepClick={handleStepClick} />
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          gap: 3,
          height: "calc(100% - 200px)",
          bgcolor: "#F8FAFC",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 3,
            borderRight: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {steps[activeStep].component}
          </Paper>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.04),
                },
              }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <LoadingButton
                variant="contained"
                onClick={handleSubmit}
                loading={saving}
                startIcon={<Icon>save</Icon>}
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                }}
              >
                Create
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<Icon>arrow_forward</Icon>}
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                }}
              >
                Continue
              </Button>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            width: "40%",
            p: 3,
            bgcolor: "white",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
            boxShadow: "-1px 0 3px rgba(0,0,0,0.05)",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: selectedBom ? 'flex-start' : 'center',
            overflow: 'auto',
            height: '100%',
          }}
        >
          {selectedBom ? (
            <POSSellableItemSummary
              formData={formData}
              selectedRecipe={selectedBom}
              modifiedIngredients={modifiedIngredients}
              modifiedProcessedItems={modifiedProcessedItems}
              multiplier={multiplier}
              activeStep={activeStep}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 4,
                minHeight: '400px',
                gap: 2,
              }}
            >
              <Icon
                sx={{
                  fontSize: 72,
                  color: alpha(theme.palette.primary.main, 0.15),
                }}
              >
                restaurant_menu
              </Icon>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                No Recipe Selected
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 300,
                }}
              >
                Select a recipe from the left panel to see the production summary and cost breakdown
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
